import { IconDownload } from '@developerz/ui';
import { createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { InviteForm } from '../components/InviteForm';
import { InviteSharesNote } from '../components/InviteSharesNote';
import { MemberRow } from '../components/MemberRow';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { PendingInvitations } from '../components/PendingInvitations';
import { QueryBoundary } from '../components/QueryBoundary';
import { Skeleton } from '../components/Skeleton';
import { Toast } from '../components/Toast';
import { Button } from '../components/ui/Button';
import { t } from '../i18n';
import { ApiError, errorReason } from '../lib/api';
import { createConfirmGate } from '../lib/confirm-gate';
import { displayLabelFor } from '../lib/display-label';
import { downloadBlob } from '../lib/download-blob';
import { membersCsv } from '../lib/members-csv';
import {
  canManageAny,
  type OrgMemberRow,
  type OrgRole,
  removeMember,
  useOrgInvitations,
  useOrgMembers,
} from '../lib/org-members';
import { useMe } from '../lib/session';
import { createToast } from '../lib/toast';

// Account → Members (#308, control-plane rebuild): the org roster as a
// .row-list panel with invite / change-role / remove. The server owns the RBAC
// (canManageMember); the UI mirrors it only to hide controls the caller plainly
// can't use, and surfaces every server refusal (e.g. last-owner) as an alert.
// Data-fetching container only — the invite form and each row are
// presentational (<InviteForm/>, <MemberRow/>) so their reactive scope reads
// busy/actor from props live.

/**
 * Turn a failed roster mutation into something a maintainer can act on.
 *
 * The invite 404 is the one worth rewording: the API says "no registered user
 * with that login", which is accurate but reads like the ENDPOINT is missing.
 * Say what to do instead — they have to sign in once first. Everything else
 * shows the server's own `detail` (a last-owner refusal, a role the actor may
 * not grant), which is already written for a human.
 */
export function inviteError(e: unknown): string {
  if (e instanceof ApiError && e.status === 404) return t('org_members.error_unknown_login');
  return errorReason(e, t('org_members.error_generic'));
}

/** Download the current roster as `members.csv` (`lib/members-csv.ts`). */
function exportCsv(rows: readonly OrgMemberRow[]): void {
  downloadBlob('members.csv', new Blob([membersCsv(rows)], { type: 'text/csv;charset=utf-8' }));
}

export function OrgMembers(props: { token?: string | null } = {}): JSX.Element {
  const me = useMe(props.token);
  const members = useOrgMembers(props.token);
  const invitations = useOrgInvitations(props.token);
  const actorRole = (): OrgRole | null => (me.data?.role as OrgRole | undefined) ?? null;
  // Display label for the active org (#1153). `me.data?.github_login` is the
  // ACTIVE account's login (the `/v1/me` view re-binds it to the scoped
  // tenant), so it keys on the right value for `displayLabelFor`. We surface
  // it in the Members page description (a static label, not live state, so not
  // the eyebrow and its live dot) — the production `developerz-ai` account
  // renders as `developerz-ai-internal`.
  const accountLabel = (): string => displayLabelFor(me.data?.github_login ?? '');

  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const toast = createToast();
  const flash = (msg: string): void => toast.notify(msg);

  // `inviteError` reads a 404 as "that login must sign in first" — true for the
  // invite endpoint, wrong for a role change / removal (there a 404 is the row's
  // own detail). So the default maps generically; only InviteForm opts into it.
  const run = async (
    fn: () => Promise<void>,
    successMsg?: string,
    mapError: (e: unknown) => string = (e) => errorReason(e, t('org_members.error_generic')),
  ): Promise<void> => {
    if (busy()) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (successMsg) flash(successMsg);
    } catch (e) {
      setError(mapError(e));
    } finally {
      await Promise.all([members.refetch(), invitations.refetch()]);
      setBusy(false);
    }
  };

  // Remove / Leave ASK first; the DELETE leaves from the dialog (row 17-10).
  const removing = createConfirmGate<OrgMemberRow>({
    act: (m) => run(() => removeMember(m.user_id, props.token), t('org_members.toast_removed')),
  });
  // The LAST asked row: `pending()` empties as the act settles, and the dialog
  // keeps rendering through its close animation.
  const asked = createMemo<OrgMemberRow | null>((last) => removing.pending() ?? last, null);
  const leaving = (): boolean => asked()?.is_self === true;

  return (
    <>
      <PageHeader
        title={t('org_members.title')}
        desc={
          accountLabel() === ''
            ? t('org_members.hint')
            : t('org_members.hint_org', { org: accountLabel() })
        }
        actions={
          <Show when={(members.data?.length ?? 0) > 0}>
            <Button variant="outline" onClick={() => exportCsv(members.data ?? [])}>
              <IconDownload />
              {t('org_members.export_csv')}
            </Button>
          </Show>
        }
      />

      <Show when={error()}>
        <div class="alert alert--destructive" role="alert">
          <p class="alert__title">{error()}</p>
        </div>
      </Show>

      <Show when={actorRole() && canManageAny(actorRole() as OrgRole)}>
        <InviteForm
          actor={actorRole() as OrgRole}
          busy={busy()}
          token={props.token}
          run={(fn, successMsg) => run(fn, successMsg, inviteError)}
        />
        <Show when={me.data?.is_platform_admin === true}>
          <InviteSharesNote />
        </Show>
        <PendingInvitations
          invitations={invitations.data ?? []}
          busy={busy()}
          token={props.token}
          run={run}
        />
      </Show>

      <QueryBoundary
        query={members}
        isEmpty={(rows) => rows.length === 0}
        loading={<Skeleton height="12rem" />}
        empty={<EmptyState message={t('org_members.empty')} />}
      >
        {(rows) => (
          <Panel title={t('org_members.roster')}>
            <ul class="row-list">
              <For each={rows}>
                {(m) => (
                  <MemberRow
                    member={m}
                    actor={actorRole()}
                    busy={busy()}
                    token={props.token}
                    run={run}
                    onAskRemove={removing.ask}
                  />
                )}
              </For>
            </ul>
          </Panel>
        )}
      </QueryBoundary>

      <ConfirmDialog
        open={removing.pending() !== null}
        title={
          leaving()
            ? t('org_members.leave_title', { org: accountLabel() })
            : t('org_members.remove_title', { login: asked()?.login ?? '' })
        }
        desc={leaving() ? t('org_members.leave_desc') : t('org_members.remove_desc')}
        cancelLabel={t('org_members.confirm_cancel')}
        actionLabel={leaving() ? t('org_members.leave_confirm') : t('org_members.remove_confirm')}
        destructive
        onCancel={removing.cancel}
        onConfirm={() => void removing.confirm()}
      />

      <Toast message={toast.toast()?.message ?? null} onDismiss={toast.dismiss} />
    </>
  );
}
